import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { Profile } from "@/types";
import { useAuth } from "@/providers/AuthProvider";
import { useThemeColors } from "@/providers/ThemeProvider";

/**
 * User selector item
 */
interface UserSelectorItemProps {
  user: Profile;
  isSelected: boolean;
  onSelect: () => void;
  colors: ReturnType<typeof useThemeColors>;
}

const UserSelectorItem = ({
  user,
  isSelected,
  onSelect,
  colors,
}: UserSelectorItemProps) => (
  <Pressable
    onPress={onSelect}
    className="px-4 py-3 flex-row items-center active:opacity-70"
    style={{
      backgroundColor: isSelected ? "#EDE9FE" : colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    }}
    accessibilityRole="button"
    accessibilityLabel={`Selecteer ${user.full_name}`}
  >
    <View
      className="w-10 h-10 rounded-full items-center justify-center mr-3"
      style={{ backgroundColor: isSelected ? "#7C3AED" : colors.primaryLight }}
    >
      <Text
        className="font-bold text-base"
        style={{ color: isSelected ? "#FFFFFF" : colors.primary }}
      >
        {(user.full_name || "?").charAt(0).toUpperCase()}
      </Text>
    </View>
    <View className="flex-1">
      <Text className="text-base font-medium" style={{ color: colors.text }}>
        {user.full_name}
      </Text>
      <Text className="text-sm" style={{ color: colors.textSecondary }}>
        {user.email}
      </Text>
    </View>
    {isSelected && (
      <Ionicons name="checkmark-circle" size={24} color="#7C3AED" />
    )}
  </Pressable>
);

/**
 * Merge stats display
 */
interface MergeStatsProps {
  stats: Record<string, number>;
  colors: ReturnType<typeof useThemeColors>;
}

const MergeStats = ({ stats, colors }: MergeStatsProps) => {
  const statLabels: Record<string, string> = {
    team_memberships: "Team lidmaatschappen",
    messages: "Chatberichten",
    notifications: "Notificaties",
    score_entries: "Score vermeldingen",
    challenge_submissions: "Challenge inzendingen",
    challenge_completions: "Challenge voltooiingen",
    calendar_exports: "Agenda exports",
    files: "Bestanden",
    activity_logs: "Activiteiten",
    commission_members: "Commissie lidmaatschappen",
  };

  const totalItems = Object.values(stats).reduce((sum, count) => sum + count, 0);

  if (totalItems === 0) {
    return (
      <View className="p-4 rounded-xl" style={{ backgroundColor: colors.surface }}>
        <Text style={{ color: colors.textSecondary }}>
          Geen data om samen te voegen
        </Text>
      </View>
    );
  }

  return (
    <View className="rounded-xl overflow-hidden" style={{ backgroundColor: colors.surface }}>
      <View className="px-4 py-3" style={{ backgroundColor: "#F3F4F6" }}>
        <Text className="font-semibold" style={{ color: colors.text }}>
          Data die wordt samengevoegd:
        </Text>
      </View>
      {Object.entries(stats).map(([key, count]) => {
        if (count === 0) return null;
        return (
          <View
            key={key}
            className="px-4 py-2.5 flex-row justify-between"
            style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}
          >
            <Text style={{ color: colors.textSecondary }}>
              {statLabels[key] || key}
            </Text>
            <Text className="font-medium" style={{ color: colors.text }}>
              {count}
            </Text>
          </View>
        );
      })}
      <View className="px-4 py-3 flex-row justify-between" style={{ backgroundColor: "#EDE9FE" }}>
        <Text className="font-semibold" style={{ color: "#5B21B6" }}>
          Totaal
        </Text>
        <Text className="font-bold" style={{ color: "#5B21B6" }}>
          {totalItems} items
        </Text>
      </View>
    </View>
  );
};

/**
 * Merge users screen
 * Allows super admins to merge a dependent user into a real account
 */
export default function MergeUsersScreen() {
  const { dependentId } = useLocalSearchParams<{ dependentId: string }>();
  const router = useRouter();
  const { user: currentUser } = useAuth();
  const colors = useThemeColors();

  const [dependent, setDependent] = useState<Profile | null>(null);
  const [targetUsers, setTargetUsers] = useState<Profile[]>([]);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [mergeStats, setMergeStats] = useState<Record<string, number> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isMerging, setIsMerging] = useState(false);

  /**
   * Fetch dependent and potential target users
   */
  const fetchData = useCallback(async () => {
    if (!dependentId) return;

    try {
      // Fetch dependent profile
      const { data: dependentData, error: dependentError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", dependentId)
        .eq("is_dependent", true)
        .single();

      if (dependentError || !dependentData) {
        Alert.alert("Fout", "Kind gebruiker niet gevonden");
        router.back();
        return;
      }

      setDependent(dependentData);

      // Fetch all non-dependent users as potential targets
      const { data: usersData, error: usersError } = await supabase
        .from("profiles")
        .select("*")
        .eq("is_dependent", false)
        .not("email", "is", null)
        .order("full_name", { ascending: true });

      if (usersError) {
        console.error("Error fetching users:", usersError);
        return;
      }

      setTargetUsers(usersData || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dependentId, router]);

  /**
   * Fetch merge statistics when target is selected
   */
  const fetchMergeStats = useCallback(async (targetId: string) => {
    if (!dependentId) return;

    setIsLoadingStats(true);

    try {
      // Count items in each table
      const [
        teamMembers,
        messages,
        notifications,
        scoreHistory,
        challengeSubmissions,
        challengeCompletions,
        calendarExports,
        files,
        activityLogs,
        commissionMembers,
      ] = await Promise.all([
        supabase.from("team_members").select("id", { count: "exact", head: true }).eq("user_id", dependentId),
        supabase.from("messages").select("id", { count: "exact", head: true }).eq("sender_id", dependentId),
        supabase.from("notifications").select("id", { count: "exact", head: true }).eq("user_id", dependentId),
        supabase.from("score_history").select("id", { count: "exact", head: true }).eq("awarded_to", dependentId),
        supabase.from("challenge_submissions").select("id", { count: "exact", head: true }).eq("submitted_by", dependentId),
        supabase.from("challenge_completions").select("id", { count: "exact", head: true }).eq("completed_by", dependentId),
        supabase.from("calendar_exports").select("id", { count: "exact", head: true }).eq("user_id", dependentId),
        supabase.from("files").select("id", { count: "exact", head: true }).eq("uploaded_by", dependentId),
        supabase.from("activity_logs").select("id", { count: "exact", head: true }).eq("user_id", dependentId),
        supabase.from("commission_members").select("id", { count: "exact", head: true }).eq("user_id", dependentId),
      ]);

      setMergeStats({
        team_memberships: teamMembers.count || 0,
        messages: messages.count || 0,
        notifications: notifications.count || 0,
        score_entries: scoreHistory.count || 0,
        challenge_submissions: challengeSubmissions.count || 0,
        challenge_completions: challengeCompletions.count || 0,
        calendar_exports: calendarExports.count || 0,
        files: files.count || 0,
        activity_logs: activityLogs.count || 0,
        commission_members: commissionMembers.count || 0,
      });
    } catch (error) {
      console.error("Error fetching merge stats:", error);
    } finally {
      setIsLoadingStats(false);
    }
  }, [dependentId]);

  /**
   * Handle target user selection
   */
  const handleSelectTarget = (userId: string) => {
    setSelectedTargetId(userId);
    fetchMergeStats(userId);
  };

  /**
   * Execute the merge
   */
  const handleMerge = async () => {
    if (!dependent || !selectedTargetId || !currentUser) return;

    const targetUser = targetUsers.find((u) => u.id === selectedTargetId);
    if (!targetUser) return;

    Alert.alert(
      "Samenvoegen Bevestigen",
      `Weet je zeker dat je "${dependent.full_name}" wilt samenvoegen met "${targetUser.full_name}"?\n\nDit kan niet ongedaan worden gemaakt!`,
      [
        { text: "Annuleren", style: "cancel" },
        {
          text: "Samenvoegen",
          style: "destructive",
          onPress: async () => {
            setIsMerging(true);

            try {
              // Call the merge function
              const { data, error } = await supabase.rpc("merge_users", {
                p_from_dependent_id: dependentId,
                p_to_user_id: selectedTargetId,
                p_performed_by: currentUser.id,
              });

              if (error) {
                console.error("Merge error:", error);
                throw error;
              }

              Alert.alert(
                "Succes",
                `${dependent.full_name} is samengevoegd met ${targetUser.full_name}. Alle data is overgedragen.`,
                [
                  {
                    text: "OK",
                    onPress: () => router.replace("/(super-admin)/users"),
                  },
                ]
              );
            } catch (error: any) {
              console.error("Merge failed:", error);
              Alert.alert(
                "Fout",
                error.message || "Samenvoegen mislukt. Probeer het opnieuw."
              );
            } finally {
              setIsMerging(false);
            }
          },
        },
      ]
    );
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ActivityIndicator size="large" color="#7C3AED" />
        <Text className="mt-2" style={{ color: colors.textSecondary }}>
          Laden...
        </Text>
      </SafeAreaView>
    );
  }

  if (!dependent) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <Ionicons name="alert-circle-outline" size={48} color={colors.textTertiary} />
        <Text className="mt-2" style={{ color: colors.textTertiary }}>
          Kind gebruiker niet gevonden
        </Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      <ScrollView 
        className="flex-1" 
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: selectedTargetId ? 100 : 20 }}
      >
        {/* Source (Dependent) Info */}
        <View className="p-4">
          <Text
            className="text-sm font-semibold uppercase tracking-wide mb-2"
            style={{ color: colors.textSecondary }}
          >
            Van (Kind)
          </Text>
          <View
            className="p-4 rounded-xl flex-row items-center"
            style={{ backgroundColor: "#DBEAFE" }}
          >
            <View
              className="w-12 h-12 rounded-full items-center justify-center mr-3"
              style={{ backgroundColor: "#1D4ED8" }}
            >
              <Text className="font-bold text-lg text-white">
                {(dependent.full_name || "?").charAt(0).toUpperCase()}
              </Text>
            </View>
            <View>
              <Text className="text-base font-semibold" style={{ color: "#1E40AF" }}>
                {dependent.full_name}
              </Text>
              <Text className="text-sm" style={{ color: "#3B82F6" }}>
                Kind gebruiker (geen e-mail)
              </Text>
            </View>
          </View>
        </View>

        {/* Arrow */}
        <View className="items-center py-2">
          <Ionicons name="arrow-down" size={24} color={colors.textTertiary} />
        </View>

        {/* Target User Selection */}
        <View className="px-4">
          <Text
            className="text-sm font-semibold uppercase tracking-wide mb-2"
            style={{ color: colors.textSecondary }}
          >
            Naar (Bestaand Account)
          </Text>
        </View>

        {targetUsers.length === 0 ? (
          <View className="px-4 py-8 items-center">
            <Text style={{ color: colors.textTertiary }}>
              Geen beschikbare gebruikers
            </Text>
          </View>
        ) : (
          <View className="rounded-xl mx-4 overflow-hidden" style={{ borderWidth: 1, borderColor: colors.border }}>
            {targetUsers.map((user) => (
              <UserSelectorItem
                key={user.id}
                user={user}
                isSelected={selectedTargetId === user.id}
                onSelect={() => handleSelectTarget(user.id)}
                colors={colors}
              />
            ))}
          </View>
        )}

        {/* Merge Stats */}
        {selectedTargetId && (
          <View className="p-4 mt-4">
            <Text
              className="text-sm font-semibold uppercase tracking-wide mb-2"
              style={{ color: colors.textSecondary }}
            >
              Samenvoeg Overzicht
            </Text>
            {isLoadingStats ? (
              <View className="p-4 items-center">
                <ActivityIndicator size="small" color="#7C3AED" />
              </View>
            ) : mergeStats ? (
              <MergeStats stats={mergeStats} colors={colors} />
            ) : null}
          </View>
        )}

        {/* Warning */}
        {selectedTargetId && (
          <View
            className="mx-4 p-4 rounded-xl flex-row"
            style={{ backgroundColor: "#FEF2F2" }}
          >
            <Ionicons name="warning" size={24} color="#DC2626" />
            <View className="flex-1 ml-3">
              <Text className="font-semibold" style={{ color: "#B91C1C" }}>
                Let op!
              </Text>
              <Text className="text-sm mt-1" style={{ color: "#DC2626" }}>
                Deze actie kan niet ongedaan worden gemaakt. Het kind profiel wordt
                verwijderd na het samenvoegen.
              </Text>
            </View>
          </View>
        )}
      </ScrollView>

      {/* Merge Button - Fixed Footer inside SafeAreaView */}
      {selectedTargetId && (
        <View
          className="px-4 pb-4 pt-3"
          style={{
            backgroundColor: colors.background,
            borderTopWidth: 1,
            borderTopColor: colors.border,
          }}
        >
          <Pressable
            onPress={handleMerge}
            disabled={isMerging}
            className="py-4 rounded-xl items-center active:opacity-80"
            style={{
              backgroundColor: "#7C3AED",
              opacity: isMerging ? 0.7 : 1,
            }}
            accessibilityRole="button"
            accessibilityLabel="Gebruikers samenvoegen"
          >
            {isMerging ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <View className="flex-row items-center">
                <Ionicons name="git-merge" size={20} color="#FFFFFF" />
                <Text className="text-white font-semibold text-base ml-2">
                  Samenvoegen
                </Text>
              </View>
            )}
          </Pressable>
        </View>
      )}
    </SafeAreaView>
  );
}

